<template>
  <div class="quick-quote-stop-edit-content">
    <QuickQuoteShippingPointLocationEdit
      :id="`${id}-location`"
      v-model="internalValue.location"
      class="row"
      :has-attempted-resolution="hasAttemptedResolution"
      @address-book-select="emit('address-book-select', $event)"
      @clear="emit('clear')"
    />

    <div v-if="isPickup" class="row">
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :errors="v$.shipDate.$errors"
        :input-id="`${id}-earliest-${internalValue.stopType}`"
        :label="t('quickQuote.requestedPickupDate')"
        class="w-3"
        help-text="mm/dd/yyyy hh:mm"
      >
        <RygenCalendar
          :id="inputId"
          v-model="shipDateTime"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :time-zone="internalValue.location.timeZone"
          :step-minute="15"
          date-format="mm/dd/yy"
          default-time="8am"
          hour-format="12"
          show-icon
          show-time
          :placeholder="t('quickQuote.requestedPickupDate')"
          @date-select="onEarliestAppointmentSet"
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputId }"
        :input-id="`${id}-tz-${internalValue.stopType}`"
        :label="t('common.timezones')"
        class="w-3"
        optional
      >
        <TimeZoneDropdown
          :id="inputId"
          v-bind="ariaProps"
          v-model="internalValue.location.timeZone"
          :placeholder="t('common.timezones')"
        />
      </InputWrapper>
    </div>

    <div class="row border-top">
      <AccessorialsEdit
        v-model="internalValue.requestedAccessorials"
        class="w-4"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { CancelTokenSource, default as axios } from 'axios';
import debounce from 'lodash/debounce';
import { DateTime } from 'luxon';
import type { Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  required,
  useModelValue,
  useValidations,
} from '@rygen/rygen-ui/composables';
import {
  Address,
  DailyHoursOfOperation,
  Location,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { QuickQuoteShippingPoint, ShipmentStopType } from '@/model';

import AccessorialsEdit from '@/components/accessorial/AccessorialsEdit.vue';
import QuickQuoteShippingPointLocationEdit from '@/components/quickQuote/QuickQuoteShippingPointLocationEdit.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenCalendar from '@rygen/rygen-ui/components/form/RygenCalendar.vue';
import TimeZoneDropdown from '@rygen/rygen-ui/components/form/TimeZoneDropdown.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: QuickQuoteShippingPoint,
    default: () => new QuickQuoteShippingPoint(),
  },
  hasAttemptedResolution: {
    type: Boolean,
    default: false,
  },
});
const { modelValue, id, hasAttemptedResolution } = toRefs(props);
const emit = defineEmits<{
  (event: 'address-book-select', Location: Location): void;
  (event: 'clear'): void;
  (event: 'update:model-value', modelValue: QuickQuoteShippingPoint): void;
}>();

const { t } = useI18n();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts: Partial<QuickQuoteShippingPoint>) =>
    new QuickQuoteShippingPoint(opts),
});

const isPickup = computed(
  () => internalValue.value.stopType === ShipmentStopType.PICKUP,
);

const rules = computed(() => {
  const r = {
    shipDate: {},
  };

  if (isPickup.value) {
    r.shipDate = {
      required: required(t('quickQuote.requestedPickupDate')),
    };
  }

  return r;
});
const { v$ } = useValidations(internalValue, rules);

const isTimezoneLoading = ref(false);
const timezoneCancelRequest: Ref<CancelTokenSource | undefined> = ref();

const shipDateTime = computed({
  get() {
    const shipDate = internalValue.value.shipDate;
    return shipDate ? DateTime.fromJSDate(shipDate) : undefined;
  },
  set(newVal: DateTime | undefined) {
    internalValue.value.shipDate = newVal?.toJSDate();
  },
});

const isFiconLocation = computed(
  () => !!internalValue.value.location.locationId,
);

const getDailyHoursForDay = (date: DateTime): DailyHoursOfOperation => {
  // 1-based index starting at monday
  // `dailyHoursOfOperation` starts with monday, so convert the index
  return internalValue.value.shippingPointDetails.hoursOfOperation
    .dailyHoursOfOperation[date.weekday - 1].hours;
};

const convertTimeString = (
  timeString: string,
): [hours: number, minutes: number, seconds: number] => {
  const [hoursStr, minutesStr, secondsStr] = timeString.split(':');
  const hours = Number.parseInt(hoursStr, 10);
  const minutes = Number.parseInt(minutesStr, 10);
  const seconds = Number.parseInt(secondsStr, 10);
  return [hours, minutes, seconds];
};

const onEarliestAppointmentSet = (dateTime: DateTime | undefined) => {
  if (!dateTime) {
    return;
  }

  if (!isFiconLocation) {
    return;
  }

  const dailyHoursOfOperation = getDailyHoursForDay(dateTime);
  if (!dailyHoursOfOperation.from) {
    return;
  }

  const [hour, minute, second] = convertTimeString(dailyHoursOfOperation.from);
  internalValue.value.shipDate = dateTime
    .set({ hour, minute, second })
    .setZone(internalValue.value.location.timeZone)
    .toJSDate();
};

const getTimezone = async () => {
  try {
    isTimezoneLoading.value = true;
    cancelExistingTimezoneRequest();

    const address = new Address({
      locality: internalValue.value.location.city,
      region: internalValue.value.location.state,
      postalCode: internalValue.value.location.postalCode,
      country: internalValue.value.location.country,
    });
    const enrichedAddress = await api.shippingPoint.getGeocodeData(
      address,
      timezoneCancelRequest.value,
    );
    if (enrichedAddress.timeZoneLocale) {
      internalValue.value.location.timeZone = enrichedAddress.timeZoneLocale;
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error fetching timezone', e);
  } finally {
    isTimezoneLoading.value = false;
  }
};
const getTimeZoneDebounced = debounce(getTimezone, 500);

const cancelExistingTimezoneRequest = () => {
  if (timezoneCancelRequest.value) {
    getTimeZoneDebounced.cancel();
    timezoneCancelRequest.value.cancel();
  }
  timezoneCancelRequest.value = axios.CancelToken.source();
};

watch(
  () => internalValue.value.location.postalCode,
  () => {
    if (timezoneCancelRequest.value) {
      cancelExistingTimezoneRequest();
    }
    getTimeZoneDebounced();
  },
);
</script>

<style>
.quick-quote-stop-edit-content {
  display: flex;
  flex-direction: column;
  gap: var(--gap-1);

  .row {
    display: flex;
    gap: var(--gap-1);
    padding: 0 var(--sp-6);
  }

  .border-top {
    border-top: var(--divider);
    padding-top: var(--sp-3);
  }
}
</style>
